import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  codeExists,
  createEmployeeAllowanceType,
  deleteEmployeeAllowanceType,
  listEmployeeAllowanceTypeRecords,
  syncEmployeeAllowanceTypesFromApi,
  updateEmployeeAllowanceType,
  type EmployeeAllowanceTypeRecord,
} from "@/lib/hr/employeeAllowanceTypesStore";
import { countEmployeesUsingAllowanceType } from "@/lib/hr/employeesStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { useGridPagination } from "@/hooks/useGridPagination";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CalendarIcon, Coins, Edit, Filter, LayoutGrid, List, ListOrdered, Loader2, Plus, Search, Tag, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "@/components/ui/sonner";
import { isAxiosError } from "axios";
import { slugify } from "@/lib/slugify";

const EMPLOYEE_ALLOWANCE_TYPES_PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

type AllowanceTypeSortBy =
  | "latest"
  | "oldest"
  | "name_asc"
  | "name_desc"
  | "sort_order_asc"
  | "sort_order_desc";

function allowanceTypeErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

const EmployeeAllowanceTypes = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "employee_allowance_type_can_create");
  const canUpdate = can(user, "employee_allowance_type_can_update");
  const canDelete = can(user, "employee_allowance_type_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterLabelQuery, setFilterLabelQuery] = useState("");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [onlyUsed, setOnlyUsed] = useState(false);
  const [onlyUnused, setOnlyUnused] = useState(false);
  const [sortOrderAtLeast, setSortOrderAtLeast] = useState("");
  const [sortBy, setSortBy] = useState<AllowanceTypeSortBy>("latest");
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeeAllowanceTypeRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmployeeAllowanceTypeRecord | null>(null);
  const [infoDialog, setInfoDialog] = useState<{ title: string; description: string } | null>(null);
  const [label, setLabel] = useState("");
  const [code, setCode] = useState("");
  const [isCodeManuallyEdited, setIsCodeManuallyEdited] = useState(false);
  const [sortOrder, setSortOrder] = useState("0");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const types = useMemo(() => listEmployeeAllowanceTypeRecords(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadFromApi = async () => {
      setIsBootstrapping(true);
      setIsForbidden(false);
      try {
        await syncEmployeeAllowanceTypesFromApi();
        if (mounted) {
          refresh();
        }
      } catch (error) {
        if (mounted && isAxiosError(error) && error.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error("[EmployeeAllowanceTypes] Load failed", error);
        if (mounted) {
          toast.error("Could not fetch latest allowance types. Showing cached data.");
        }
      } finally {
        if (mounted) {
          setIsBootstrapping(false);
        }
      }
    };
    void loadFromApi();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const usageByTypeId = useMemo(() => {
    const usage = new Map<string, number>();
    for (const type of types) {
      usage.set(type.id, countEmployeesUsingAllowanceType(type.id));
    }
    return usage;
  }, [types]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const labelQ = filterLabelQuery.trim().toLowerCase();
    const minSortOrder = Number(sortOrderAtLeast);
    const hasMinSort = sortOrderAtLeast.trim() !== "" && Number.isFinite(minSortOrder);
    return types.filter((t) => {
      const matchesQuery =
        !q ||
        t.label.toLowerCase().includes(q) ||
        t.code.toLowerCase().includes(q) ||
        String(t.sortOrder ?? "").includes(q);
      const matchesLabel = !labelQ || t.label.toLowerCase().includes(labelQ);
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(t.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(t.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      const usageCount = usageByTypeId.get(t.id) ?? 0;
      const matchesUsed = !onlyUsed || usageCount > 0;
      const matchesUnused = !onlyUnused || usageCount === 0;
      const matchesSortOrder = !hasMinSort || (t.sortOrder ?? 0) >= minSortOrder;
      return matchesQuery && matchesLabel && matchesUsed && matchesUnused && matchesSortOrder;
    });
  }, [
    types,
    query,
    filterLabelQuery,
    filterCreatedDate,
    quickDate,
    onlyUsed,
    onlyUnused,
    sortOrderAtLeast,
    usageByTypeId,
  ]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.label.localeCompare(a.label);
        case "name_asc":
          return a.label.localeCompare(b.label);
        case "sort_order_desc":
          return (b.sortOrder ?? 0) - (a.sortOrder ?? 0);
        case "sort_order_asc":
          return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filterLabelQuery.trim()) count += 1;
    if (filterCreatedDate) count += 1;
    if (onlyUsed) count += 1;
    if (onlyUnused) count += 1;
    if (sortOrderAtLeast.trim()) count += 1;
    return count;
  }, [filterLabelQuery, filterCreatedDate, onlyUsed, onlyUnused, sortOrderAtLeast]);

  const resetSheetFilters = () => {
    setFilterLabelQuery("");
    setFilterCreatedDate(undefined);
    setOnlyUsed(false);
    setOnlyUnused(false);
    setSortOrderAtLeast("");
  };

  const paginationKey = `${query}|${filterLabelQuery}|${filterCreatedDate?.getTime() ?? ""}|${quickDate}|${onlyUsed ? "1" : "0"}|${onlyUnused ? "1" : "0"}|${sortOrderAtLeast}|${sortBy}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: EMPLOYEE_ALLOWANCE_TYPES_PAGE_SIZE_OPTIONS,
    defaultPageSize: EMPLOYEE_ALLOWANCE_TYPES_PAGE_SIZE_OPTIONS[0],
  });

  const startCreate = () => {
    if (!canCreate) {
      toast.error("You don’t have permission to create allowance types.");
      return;
    }
    setEditing(null);
    setLabel("");
    setCode("");
    setIsCodeManuallyEdited(false);
    setSortOrder(String(types.reduce((m, t) => Math.max(m, t.sortOrder), 0) + 1));
    setOpen(true);
  };

  const startEdit = (t: EmployeeAllowanceTypeRecord) => {
    if (!canUpdate) {
      toast.error("You don’t have permission to update allowance types.");
      return;
    }
    setEditing(t);
    setLabel(t.label);
    setCode(t.code);
    setIsCodeManuallyEdited(false);
    setSortOrder(String(t.sortOrder));
    setOpen(true);
  };

  useEffect(() => {
    if (isCodeManuallyEdited) return;
    const next = slugify(label);
    setCode(next);
  }, [label, isCodeManuallyEdited]);

  const save = async () => {
    if (isSaving) return;
    if (!label.trim()) {
      toast.error("Allowance type label is required.");
      return;
    }
    if (!code.trim()) {
      toast.error("Allowance type code is required.");
      return;
    }
    if (codeExists(code, editing?.id)) {
      setInfoDialog({
        title: "Code already in use",
        description: "Another allowance type already uses this code. Choose a different one.",
      });
      return;
    }
    setIsSaving(true);
    try {
      if (editing) {
        const updateResult = await updateEmployeeAllowanceType(editing.id, {
          label,
          code,
          sortOrder: Number(sortOrder) || 0,
        });
        if (!updateResult) {
          toast.error("Allowance type not found.");
          return;
        }
        toast.success(updateResult.message || "Allowance type updated successfully.");
      } else {
        const createResult = await createEmployeeAllowanceType({
          label,
          code,
          sortOrder: Number(sortOrder) || 0,
        });
        toast.success(createResult.message);
      }
      setOpen(false);
      refresh();
    } catch (error) {
      console.error("[EmployeeAllowanceTypes] Save failed", error);
      toast.error(allowanceTypeErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const requestRemove = (t: EmployeeAllowanceTypeRecord) => {
    if (!canDelete) {
      toast.error("You don’t have permission to delete allowance types.");
      return;
    }
    const n = countEmployeesUsingAllowanceType(t.id);
    if (n > 0) {
      setInfoDialog({
        title: "Cannot delete allowance type",
        description: `${n} employee(s) still use “${t.label}” in salary allowances. Remove or change those rows first.`,
      });
      return;
    }
    setDeleteTarget(t);
  };

  const confirmRemove = async () => {
    if (!deleteTarget || isDeleting) return;
    if (!canDelete) {
      toast.error("You don’t have permission to delete allowance types.");
      return;
    }
    setIsDeleting(true);
    try {
      const result = await deleteEmployeeAllowanceType(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[EmployeeAllowanceTypes] Delete failed", error);
      toast.error(allowanceTypeErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  if (isForbidden) {
    return (
      <PermissionDenied
        title="Employee allowance types access required"
        message="You don’t have permission to view employee allowance types. Ask an administrator to grant employee_allowance_type_can_view_any."
      />
    );
  }

  return (
    <Layout title="Employee allowance types">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Employee allowance types</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Define reusable allowance names (medical, conveyance, etc.). Then pick them and enter amounts when{" "}
              <Link to="/employees/add" className="text-primary font-medium hover:underline">
                adding or editing an employee
              </Link>
              , similar to purchase cost types for products.
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={startCreate}>
              <Plus className="w-4 h-4" /> Add allowance type
            </Button>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, group…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {activeFilterCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {activeFilterCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter allowance types</SheetTitle>
                  <SheetDescription>Refine by label, created date, usage, and sort order.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Label contains</Label>
                    <Input
                      className="glass-input h-10"
                      value={filterLabelQuery}
                      onChange={(e) => setFilterLabelQuery(e.target.value)}
                      placeholder="e.g. Medical"
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Created date</Label>
                    <Popover modal={true}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "glass-input h-10 w-full justify-start text-left font-normal",
                            !filterCreatedDate && "text-muted-foreground",
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                          {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                        <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                        <div className="flex gap-2 border-t p-2">
                          <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                            Clear
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Minimum sort order</Label>
                    <Input
                      className="glass-input h-10"
                      type="number"
                      value={sortOrderAtLeast}
                      onChange={(e) => setSortOrderAtLeast(e.target.value)}
                      placeholder="e.g. 2"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>Used on employees only</span>
                      <Checkbox
                        checked={onlyUsed}
                        onCheckedChange={(checked) => {
                          const next = Boolean(checked);
                          setOnlyUsed(next);
                          if (next) {
                            setOnlyUnused(false);
                          }
                        }}
                      />
                    </label>
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>Unused only</span>
                      <Checkbox
                        checked={onlyUnused}
                        onCheckedChange={(checked) => {
                          const next = Boolean(checked);
                          setOnlyUnused(next);
                          if (next) {
                            setOnlyUsed(false);
                          }
                        }}
                      />
                    </label>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={() => resetSheetFilters()}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="eat-page-size-mobile"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={EMPLOYEE_ALLOWANCE_TYPES_PAGE_SIZE_OPTIONS}
            />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as AllowanceTypeSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="sort_order_asc">Sort order: Low to High</SelectItem>
                  <SelectItem value="sort_order_desc">Sort order: High to Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="eat-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={EMPLOYEE_ALLOWANCE_TYPES_PAGE_SIZE_OPTIONS}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as AllowanceTypeSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="sort_order_asc">Sort order: Low to High</SelectItem>
                    <SelectItem value="sort_order_desc">Sort order: High to Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit allowance type" : "New allowance type"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Label *</Label>
                <Input
                  className="glass-input h-10"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Custom duty"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Code *</Label>
                <Input
                  className="glass-input h-10"
                  value={code}
                  onChange={(e) => {
                    setIsCodeManuallyEdited(true);
                    setCode(e.target.value);
                  }}
                  placeholder="auto from label (editable)"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Sort order</Label>
                <Input className="glass-input h-10" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={() => setOpen(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void save()} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {isBootstrapping ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading allowance types…
          </div>
        ) : null}

        {!isBootstrapping && sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {types.length === 0 ? (
              <>
                No allowance types yet. Add them here, then assign amounts on{" "}
                <Link to="/employees/add" className="text-primary font-medium hover:underline">
                  Add employee
                </Link>
                .
              </>
            ) : (
              "No types match your search."
            )}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <CatalogEntityCard
                  title={t.label}
                  primaryMeta={{
                    label: "Code",
                    value: t.code,
                    icon: Tag,
                    iconClassName: "text-sky-600",
                  }}
                  secondaryMeta={{
                    label: "Sort order",
                    value: String(t.sortOrder),
                    icon: ListOrdered,
                    iconClassName: "text-emerald-600",
                  }}
                  footer={`Created: ${formatCatalogAuditDate(t.createdAt)}`}
                  footerRight={`Used: ${usageByTypeId.get(t.id) ?? 0}`}
                  onEdit={canUpdate ? () => startEdit(t) : undefined}
                  onDelete={canDelete ? () => requestRemove(t) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <Coins className="w-8 h-8 text-muted-foreground/40" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-foreground truncate">{t.label}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      Code: {t.code} · Sort order: {t.sortOrder}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Used on {usageByTypeId.get(t.id) ?? 0} employee(s)</p>
                  </div>
                  <div className="text-right ml-auto">
                    <p className="text-[11px] text-muted-foreground">Created {formatCatalogAuditDate(t.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => startEdit(t)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => requestRemove(t)}
                        aria-label="Delete allowance type"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this allowance type?"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete{" "}
                <strong className="text-foreground font-semibold">{deleteTarget.label}</strong>? This cannot be undone.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmRemove()}
        />

        <ConfirmAlertDialog
          open={!!infoDialog}
          onOpenChange={(o) => !o && setInfoDialog(null)}
          title={infoDialog?.title ?? ""}
          description={infoDialog?.description ?? ""}
          mode="alert"
          variant="warning"
          onConfirm={() => setInfoDialog(null)}
        />
      </div>
    </Layout>
  );
};

export default EmployeeAllowanceTypes;
